import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { GrowlerService } from '../../../../services/growler.service';
import { FormBuilder } from '@angular/forms';
import { MessageBoardSubscription } from '../../../../models/entities/message-board-subscription';
import { JwtLegFiClaims } from '../../../../services/auth/jwt-legfi-claims.model';
import { LegFiJwtService } from '../../../../services/auth/legfi-jwt.service';
import { MessageBoardSubscriptionService, UpdateSubscriptionRequest } from '../../../../services/message-board/message-board-subscription.service';
import { Router } from '@angular/router';

export interface SubscriptionDialogInput
{
    subscription: MessageBoardSubscription;
    isUserSubscribed: boolean;
}

export interface SubscriptionDialogOutput
{
    isUserSubscribed: boolean;
    success: boolean;
    frequency?: string;
}

@Component({
    selector: 'app-subscription-dialog',
    templateUrl: './subscription-dialog.component.html',
})
export class SubscriptionDialogComponent implements OnInit
{
    title = '';
    form = this._fb.group({
        frequency: ['daily'],
    });
    isSaving = false;
    jwt: JwtLegFiClaims = LegFiJwtService.read();

    constructor(
            private _dialogRef: MatDialogRef<SubscriptionDialogComponent>,
            private _fb: FormBuilder,
            private _growler: GrowlerService,
            private _router: Router,
            private _subService: MessageBoardSubscriptionService,
            @Inject(MAT_DIALOG_DATA) public data: SubscriptionDialogInput,
    ) {
    }

    ngOnInit(): void {
        if (this.data.subscription.subscribableType === 'thread') {
            this.title = this.data.isUserSubscribed ?
                    'Manage thread notifications' : 'Subscribe to this thread';
        } else {
            this.title = this.data.isUserSubscribed ?
                    'Manage board notifications' : 'Subscribe to all threads in this board';
        }

        if (this.data.subscription.frequency) {
            this.form.get('frequency').setValue(this.data.subscription.frequency);
        }
    }

    onSubmit() {
        this.form.get('frequency').value === 'unsubscribe' ? this.unsubscribe() : this.subscribe();
    }

    /** User was subscribed; unsub them */
    unsubscribe() {
        this.isSaving = true;
        this._subService.deleteSubscription(this.data.subscription.id).subscribe(
                {
                    next: () => {
                        this._growler.success('Success',
                                'You have been unsubscribed from this ' + this.data.subscription.subscribableType);
                        this.isSaving = false;

                        this._dialogRef.close(<SubscriptionDialogOutput>{
                            isUserSubscribed: false,
                            success: true,
                        });
                    },
                    error: (error) => {
                        this._growler.error('Error', 'There was a problem unsubscribing.');
                    },
                },
        );
    }

    /** User is subbing or updating */
    subscribe() {
        this.isSaving = true;
        const request: UpdateSubscriptionRequest = {
            frequency: this.form.get('frequency').value,
        };

        this.data.subscription.subscribableType === 'board' ?
                request.boardId = this.data.subscription.subscribableId :
                request.threadId = this.data.subscription.subscribableId;

        this._subService.upsertSubscription(request)
                .subscribe(
                {
                    next: () => {
                        this._growler.success(
                                'Success',
                                'You are subscribed to this ' + this.data.subscription.subscribableType,
                        );

                        this.isSaving = false;
                        this._dialogRef.close(<SubscriptionDialogOutput>{
                            frequency: this.form.get('frequency').value,
                            isUserSubscribed: true,
                            success: true,
                        });
                    },
                    error: (error) => {
                        this._growler.error('Error', 'There was a problem loading the board.');
                    },
                },
        );
    }

    /** They are routing to the user notifications page; close the dialog first */
    closeAndRoute() {
        this._dialogRef.close();
        this._router.navigate(['/app/settings/user/notifications']);
    }
}
